// src/data/questions/technicalQuestions.ts
// Technical Safeguards Questions (Q26-Q32)
// TODO: Add tooltip content from navigator-improvements-guide/data/tooltip-content-Q21-Q30.md and Q31-Q40.md

import { Question } from '../../types';

export const technicalQuestions: Question[] = [
  {
    id: 'Q26',
    section: 'technical',
    question: 'Does every user have a unique user ID (no shared accounts)?',
    type: 'single',
    options: [
      { value: 'unique', label: 'Yes, unique IDs for all users (no sharing)', description: '', points: 3 },
      { value: 'mostly', label: 'Mostly unique, but some shared accounts exist', description: '', points: 1 },
      { value: 'shared', label: 'Many shared accounts', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '§164.312(a)(2)(i) - Unique user identification (REQUIRED)',
    required: true,
    weight: 3,
    tooltipContent: {
      plainEnglish: "Every person who accesses PHI must have their own unique user ID so their actions can be tracked. Shared accounts (multiple people using the same login) are prohibited because: (1) you can't identify who accessed/modified what data, (2) accountability disappears (who made the error?), and (3) you can't revoke access for one person without affecting others. Shared accounts are very common in healthcare. \"We all use the nurse station computer with password 'clinic123'\" = violation. Generic accounts like \"frontdesk\" or \"doctor\" = violation. Every employee needs their own username/password (or federated login like Okta/SAML).",
      hipaaReference: "§164.312(a)(2)(i) - Unique User Identification (REQUIRED). Status: Required. Effective Date: Security Rule (2003). Shared accounts are cited in ~60% of HIPAA audits. Without unique IDs, you can't investigate who accessed what during a breach (audit logs become meaningless). Shared accounts prevent employee accountability (nobody is responsible). Shared account violations are easy for OCR to document (one audit query to your system shows multiple people logged in as \"nurse1\").",
      realWorldExample: "A clinic has 5 front desk staff sharing one login \"frontdesk\" on the reception computer. Violation. Fix: each person gets their own username/password (or Smart Card + PIN). Another clinic has a shared account \"doctor1\" used by multiple physicians. When a patient sues claiming a doctor altered records, they can't identify which doctor (all entries show \"doctor1\"). Violation and serious liability. Fix: each provider gets unique ID. A hospital implements federated login: every employee logs in with their network credentials (username/password or smart card). Each system (EHR, labs, pharmacy) recognizes the same unique ID. Access is consistent and trackable.",
      estimatedCost: {
        small: "Cost included in EHR (most modern EHRs have this)",
        medium: "$5,000-20,000 setup; $500-2,000/month (directory service)",
        large: "$2,000-10,000 (federated login implementation)"
      },
      implementationSteps: [
        "Assessment: 1 week",
        "Implementation: 2-4 weeks (depends on system complexity)",
        "Training: 1 week"
      ],
      commonMistakes: [
        "Allowing shared accounts to save money on licenses",
        "Not verifying all systems use unique IDs",
        "Forgetting to audit for shared accounts regularly",
        "Not having a process for creating unique IDs for new employees"
      ],
      resourceLinks: [
        { title: "HHS Unique User Identification", url: "https://www.hhs.gov/hipaa/for-professionals/security/laws-regulations/index.html", type: "external" }
      ],
      timeToComplete: {
        small: "2-4 weeks",
        medium: "4-8 weeks",
        large: "6-12 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "IT Administrator",
        "EHR System Administrator",
        "Network Administrator",
        "Security Manager"
      ],
      whereToLook: [
        "User account lists in EHR and other systems",
        "Active Directory or identity management system",
        "System access logs",
        "User provisioning procedures"
      ],
      howToVerify: "Request a list of all user accounts in systems that access PHI. Look for generic accounts (\"nurse1\", \"frontdesk\", \"doctor\") or accounts shared by multiple people. Check if each employee has their own unique username.",
      whatToDocument: "Number of unique user accounts vs. number of employees, list of any shared accounts found, user provisioning process, account creation/deletion procedures"
    }
  },
  {
    id: 'Q27',
    section: 'technical',
    question: 'Do you have multi-factor authentication (MFA) enabled?',
    type: 'single',
    options: [
      { value: 'all', label: 'Yes, MFA required for all users', description: '', points: 3 },
      { value: 'privileged', label: 'MFA for privileged users only', description: '', points: 2 },
      { value: 'no', label: 'No MFA implemented', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '2026 HIPAA Security Rule update - MFA now mandatory',
    required: true,
    weight: 3,
    tooltipContent: {
      plainEnglish: "Multi-factor authentication (MFA) requires users to provide two or more authentication factors (something you know: password; something you have: phone/token; something you are: biometric). MFA prevents unauthorized access even if someone knows the password. For example, attacker steals a clinician's password but can't log in because they don't have the clinician's phone to receive the MFA code. As of January 2025, MFA is REQUIRED for all users under the updated HIPAA Security Rule (effective 2026). This is one of the most significant updates to HIPAA in decades. Organizations must have MFA for all users accessing PHI. No exceptions.",
      hipaaReference: "2026 HIPAA Security Rule Update - MFA now mandatory for all users; §164.312(a)(2)(i) (as updated). Status: Required (effective January 2025; compliance deadline anticipated for 2026). Effective Date: January 2025 (new requirement); compliance grace period expected. 99% of breaches involving compromised credentials could be prevented by MFA. Ransomware attacks targeting healthcare often start with stolen credentials; MFA blocks this vector. This is regulatory change; organizations not implementing MFA by deadline face enforcement action.",
      realWorldExample: "A clinic implements MFA using Google Authenticator: each employee installs the app on their phone, scans a QR code during EHR setup, and is required to enter the 6-digit code from the app when logging in. Rollout takes 2 weeks; support tickets are high initially but drop off quickly. An attacker steals a clinician's EHR password but can't log in without the clinician's phone. Breach prevented. A hospital implements conditional MFA: if you log in from the expected location/device, no MFA required (reduces friction); if you log in from a new location or device, MFA required. Balances security and usability.",
      estimatedCost: {
        small: "$0-500 (if already in identity platform)",
        medium: "$2-10/user/month (federated MFA via Okta, Azure AD)",
        large: "$5,000-15,000 implementation + $2,000-5,000 training"
      },
      implementationSteps: [
        "Planning and vendor selection: 2-4 weeks",
        "Pilot (small group): 2-4 weeks",
        "Full rollout: 4-8 weeks",
        "Total: 8-16 weeks"
      ],
      commonMistakes: [
        "Implementing MFA only for administrators, not all users",
        "Using SMS-only MFA (less secure than TOTP apps)",
        "Not having a backup authentication method",
        "Poor user training leading to lockouts and support tickets"
      ],
      resourceLinks: [
        { title: "HHS Access Control", url: "https://www.hhs.gov/hipaa/for-professionals/security/laws-regulations/index.html", type: "external" }
      ],
            timeToComplete: {
        small: "4-8 weeks",
        medium: "8-16 weeks",
        large: "12-20 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "IT Security Manager",
        "Identity Management Administrator",
        "EHR System Administrator",
        "Network Administrator"
      ],
      whereToLook: [
        "Identity management system (Okta, Azure AD, etc.)",
        "EHR authentication settings",
        "VPN configuration",
        "User authentication logs"
      ],
      howToVerify: "Check if users are required to provide a second factor (phone app, SMS, hardware token) when logging into systems with PHI. Test by attempting to log in with just username/password.",
      whatToDocument: "MFA implementation status, which systems require MFA, what authentication methods are used, percentage of users enrolled in MFA"
    }
  },
  {
    id: 'Q28',
    section: 'technical',
    question: 'Do you have automatic logoff for inactive sessions?',
    type: 'single',
    options: [
      { value: '15min', label: 'Yes, automatic logoff after 15 minutes or less', description: '', points: 3 },
      { value: '30min', label: 'Logoff after 30+ minutes', description: '', points: 2 },
      { value: 'no', label: 'No automatic logoff', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '§164.312(a)(2)(iii) - Automatic logoff (Addressable)',
    required: true,
    weight: 2,
    tooltipContent: {
      plainEnglish: "Automatic logoff (also called session timeout) terminates user sessions after a period of inactivity (e.g., 15 minutes of no keyboard/mouse activity). This prevents unattended workstations from being used by unauthorized people. An employee steps away for lunch and forgets to lock their computer; with automatic logoff, the session ends after 15 minutes and an unauthorized person can't use it. Recommended: 15 minutes or less. This is aggressive and may reduce usability (clinician gets logged out while reviewing a chart), but it's the most secure approach. Some organizations use 30 minutes for administrative staff but 15 minutes for clinical staff (because clinicians are in higher-risk environments).",
      hipaaReference: "§164.312(a)(2)(iii) - Automatic Logoff (Addressable; updated guidance suggests this is becoming a best practice expectation, aligned with 2026 security rule emphasis). Status: Addressable (recommended); strong industry guidance suggests should be required. Effective Date: Security Rule (2003); new 2026 guidance emphasizes automatic logoff. 20% of healthcare breaches involve unattended workstations. Automatic logoff is one of the simplest, lowest-cost controls that has a big impact. OCR increasingly expects this control; audits often flag its absence.",
      realWorldExample: "A clinic sets EHR session timeout to 15 minutes. Clinician uses EHR to review a patient chart, gets called into an exam room, leaves workstation unattended. After 15 minutes, EHR logs out. Clinician returns, logs back in, resumes work. A hospital sets different timeouts: 30 minutes for administrative staff, 15 minutes for clinical staff in public areas (hallways, nursing stations). A clinic with no timeout: employee leaves workstation logged in overnight. Visitor could access records. Breach. After incident, they implement 15-minute timeout.",
      estimatedCost: {
        small: "$0 (configuration only)",
        medium: "$0-1,000 (depends on EHR flexibility)",
        large: "$1,000-3,000 (implementation and testing)"
      },
      implementationSteps: [
        "Configuration: 1-2 weeks",
        "Testing: 1 week",
        "Rollout and training: 1-2 weeks"
      ],
      commonMistakes: [
        "Setting timeout too long (over 30 minutes)",
        "Not configuring timeout on all systems that access PHI",
        "Not testing timeout functionality",
        "Not training users on what to expect"
      ],
      resourceLinks: [
        { title: "HHS Emergency Access Procedures", url: "https://www.hhs.gov/hipaa/for-professionals/security/laws-regulations/index.html", type: "external" }
      ],
            timeToComplete: {
        small: "1-2 weeks",
        medium: "2-4 weeks",
        large: "3-6 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "IT Administrator",
        "EHR System Administrator",
        "Network Administrator",
        "Security Manager"
      ],
      whereToLook: [
        "EHR session timeout settings",
        "Windows Group Policy settings",
        "VPN timeout configuration",
        "Application-specific timeout settings"
      ],
      howToVerify: "Test by logging into systems and leaving them idle. Time how long before automatic logoff occurs. Check if timeout applies to all systems that access PHI.",
      whatToDocument: "Timeout duration for each system, which systems have automatic logoff enabled, testing results, user training completion"
    }
  },
  {
    id: 'Q29',
    section: 'technical',
    question: 'Is ePHI encrypted at rest and in transit?',
    type: 'single',
    options: [
      { value: 'both', label: 'Yes, both at rest and in transit (TLS 1.2+, AES-256)', description: '', points: 3 },
      { value: 'transit', label: 'Partial encryption (e.g., only in transit)', description: '', points: 1 },
      { value: 'no', label: 'No encryption', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '§164.312(a)(2)(iv) - Encryption (Addressable)',
    required: true,
    weight: 3,
    tooltipContent: {
      plainEnglish: "Encryption protects data by converting it to unreadable form without a decryption key. Encryption at rest protects stored data (files on disk, databases, backups); encryption in transit protects data while being transmitted (over network, internet, email). Both are critical. Many breaches occur because data was stored unencrypted on a device that was stolen or an email without encryption was intercepted. Encryption standards matter: AES-256 for symmetric encryption (encryption at rest), TLS 1.2+ for transport layer security (encryption in transit). Older standards (DES, SSL 3.0) are insecure and not acceptable. You must also manage encryption keys securely (not hardcoded in source code, properly stored and backed up).",
      hipaaReference: "§164.312(a)(2)(iv) - Encryption and Decryption (Addressable; strong guidance suggests should be required); 2026 updates emphasize encryption as baseline. Status: Addressable (but strongly recommended/becoming expected); aligned with 2026 security rule emphasis. Effective Date: Security Rule (2003); encryption emphasis (2024-2026). 30% of healthcare breaches involve unencrypted data. Regulatory pressure increasing: encryption moving from \"addressable\" to \"expected\". Insurance companies increasingly require encryption for coverage.",
      realWorldExample: "A clinic stores patient records in an Excel file on an employee's laptop with no encryption. Laptop is stolen. If files were encrypted, data wouldn't be accessible to thief. Without encryption, breach. A hospital transmits patient data to insurance company over unencrypted email attachment. Network administrator intercepts email and extracts patient data. With encryption, this would have been impossible. A clinic uses AWS for data storage. AWS S3 encryption is enabled by default using AES-256. Data at rest is encrypted. All data transmitted to/from AWS uses TLS 1.2+. Compliant. Another clinic transmits data to third-party vendor over FTP (unencrypted). Fix: switch to SFTP (encrypted) or use VPN + FTP.",
      estimatedCost: {
        small: "$0 (usually included/default in cloud)",
        medium: "$0-1,000 (often built-in features)",
        large: "$5,000-20,000/year (key management service)"
      },
      implementationSteps: [
        "Assessment and planning: 1-2 weeks",
        "Implementation: 2-6 weeks (depends on scope)",
        "Testing and validation: 1-2 weeks"
      ],
      commonMistakes: [
        "Encrypting only some data, not all PHI",
        "Using weak encryption standards (DES, SSL 3.0)",
        "Poor encryption key management",
        "Not encrypting data in transit (email, file transfers)"
      ],
      resourceLinks: [
        { title: "HHS Encryption Guidance", url: "https://www.hhs.gov/hipaa/for-professionals/security/guidance/cybersecurity/index.html", type: "external" }
      ],
            timeToComplete: {
        small: "2-4 weeks",
        medium: "4-8 weeks",
        large: "6-12 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "IT Security Manager",
        "Database Administrator",
        "Cloud Administrator",
        "Network Administrator"
      ],
      whereToLook: [
        "Database encryption settings",
        "Cloud storage encryption configuration",
        "File system encryption status",
        "Network transmission protocols"
      ],
      howToVerify: "Check if databases use encryption at rest (TDE, etc.). Verify cloud storage has encryption enabled. Test if data transmissions use HTTPS/TLS. Review email encryption for PHI.",
      whatToDocument: "Encryption status for all systems storing PHI, encryption standards used (AES-256, TLS 1.2+), key management procedures, encryption verification testing results"
    }
  },
  {
    id: 'Q30',
    section: 'technical',
    question: 'Do you have audit logging for all access to ePHI?',
    type: 'single',
    options: [
      { value: 'comprehensive', label: 'Yes, comprehensive logging with 6-year retention', description: '', points: 3 },
      { value: 'partial', label: 'Partial logging (some systems only)', description: '', points: 1 },
      { value: 'no', label: 'No audit logging', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '§164.312(b) - Audit controls (REQUIRED)',
    required: true,
    weight: 3,
    tooltipContent: {
      plainEnglish: "Audit logging records every access to PHI: who accessed what, when, where they accessed from, and what they did. Logs must be comprehensive (capture all access, not just some systems), retained for at least 6 years (HIPAA requirement), and regularly reviewed for suspicious activity. Audit logs are your primary evidence of what happened during a breach investigation. Many organizations have audit logs turned off or only log errors. You need logs of all access attempts (successful and failed), all data modifications, all exports/downloads. Logs should be protected from tampering and centrally stored (not left on individual servers where they can be deleted).",
      hipaaReference: "§164.312(b) - Audit Controls (REQUIRED); must log all access to ePHI. Status: Required. Effective Date: Security Rule (2003). Audit logs are required by law; absence is automatic HIPAA violation. Logs are your only evidence during breach investigation; without them, you can't determine scope of breach or who was responsible. Logs must be 6-year retention; destroying logs after 1-2 years = violation. OCR specifically reviews audit logs during investigations; organizations with comprehensive logs have better outcomes.",
      realWorldExample: "A clinic's EHR has audit logging enabled. Every access is logged with username, timestamp, patient ID, action taken. When a clinician accesses a celebrity patient's record inappropriately, the log shows the access and the clinician is disciplined. A hospital discovers an employee has accessed records of ex-partners (snooping). They review 6 years of audit logs, identify all inappropriate accesses, notify affected patients, and submit to HHS. Without logs, they wouldn't know the scope (10 patients? 100 patients? 1,000 patients?). A clinic has audit logs but they're stored on a local hard drive that fails. Years of logs are lost. When OCR audits, the clinic can't produce logs. Automatic violation. Fix: centralized log storage with backups.",
      estimatedCost: {
        small: "$0 (EHR logging built-in)",
        medium: "$2,000-10,000/year (log management tool)",
        large: "$10,000-50,000+/year (enterprise SIEM)"
      },
      implementationSteps: [
        "Enable EHR logging: 1-2 weeks",
        "Log aggregation tool selection and implementation: 4-8 weeks",
        "Log review process establishment: 1-2 weeks"
      ],
      commonMistakes: [
        "Not enabling logging on all systems that access PHI",
        "Storing logs locally where they can be lost or tampered with",
        "Not retaining logs for the required 6 years",
        "Never reviewing logs for suspicious activity"
      ],
      resourceLinks: [
        { title: "HHS Audit Controls", url: "https://www.hhs.gov/hipaa/for-professionals/security/laws-regulations/index.html", type: "external" }
      ],
            timeToComplete: {
        small: "2-4 weeks",
        medium: "6-12 weeks",
        large: "12-16 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "IT Security Manager",
        "EHR System Administrator",
        "Database Administrator",
        "Log Management Administrator"
      ],
      whereToLook: [
        "EHR audit log settings",
        "Centralized log management system",
        "Database audit settings",
        "System access logs"
      ],
      howToVerify: "Check if audit logging is enabled on all systems that access PHI. Verify logs capture who, what, when, where. Confirm logs are retained for 6+ years and stored securely.",
      whatToDocument: "Audit logging status for each system, log retention period, log storage location and security, log review procedures and frequency"
    }
  },
  {
    id: 'Q31',
    section: 'technical',
    question: 'Do you have mechanisms to verify ePHI has not been altered/destroyed?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes, integrity controls (checksums, digital signatures)', description: '', points: 3 },
      { value: 'basic', label: 'Basic version control', description: '', points: 1 },
      { value: 'no', label: 'No integrity verification', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '§164.312(c)(1) - Integrity controls (Addressable)',
    required: true,
    weight: 2,
    tooltipContent: {
      plainEnglish: "Integrity controls verify that data hasn't been tampered with, accidentally corrupted, or deliberately modified. This includes: (1) checksums/hashing (mathematical signature of data that changes if data is modified), (2) digital signatures (cryptographic proof of identity and authenticity), (3) version control (history of changes), and (4) access logs (who modified what when). For example, if a clinician's note is modified after being written, integrity controls should detect this. Integrity isn't just about malicious tampering. It also covers accidental corruption: a database corrupts a field, a file transfer fails and corrupts data, or a backup is unreadable. You need mechanisms to detect these problems.",
      hipaaReference: "§164.312(c)(1) - Integrity Controls (Addressable); includes mechanisms to verify PHI authenticity and integrity. Status: Addressable (but strongly expected). Effective Date: Security Rule (2003). Undetected data corruption can go unnoticed for months (patient receives wrong treatment based on corrupted data). Malicious tampering (altering medical records) is difficult to detect without integrity controls. Integrity controls provide legal evidence in disputes (digital signature proves clinician wrote the note). OCR increasingly expects integrity controls; absence is cited in ~40% of audits.",
      realWorldExample: "A clinic exports patient records to a PDF and wants to verify the file wasn't corrupted during export. They use a SHA-256 hash: if the file is modified even one byte, the hash changes. Recipient can verify file integrity by comparing hash. A hospital signs discharge summaries with a digital signature. If anyone later alters the document, the signature becomes invalid (proof of tampering). An EHR with version control allows tracking of changes: who wrote the original note, who made edits, what was changed, when. If a clinician claims their note was altered, version history proves/disproves it. A clinic uses file integrity monitoring on critical configuration files; if a hacker modifies a configuration file, the monitor alerts the security team immediately.",
      estimatedCost: {
        small: "$0 (usually built-in database features)",
        medium: "$0-5,000 (version control implementation)",
        large: "$2,000-10,000/year (file integrity monitoring)"
      },
      implementationSteps: [
        "Assessment and planning: 1-2 weeks",
        "Implementation: 2-4 weeks",
        "Testing and validation: 1 week"
      ],
      commonMistakes: [
        "Not implementing integrity controls for all PHI",
        "Using weak hashing algorithms (MD5 instead of SHA-256)",
        "Not protecting integrity verification mechanisms from tampering",
        "Not regularly testing integrity controls"
      ],
      resourceLinks: [
        { title: "HHS Integrity Controls", url: "https://www.hhs.gov/hipaa/for-professionals/security/laws-regulations/index.html", type: "external" }
      ],
            timeToComplete: {
        small: "2-4 weeks",
        medium: "4-6 weeks",
        large: "6-10 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "IT Security Manager",
        "Database Administrator",
        "EHR System Administrator",
        "Backup Administrator"
      ],
      whereToLook: [
        "Database integrity check settings",
        "File integrity monitoring systems",
        "Version control systems",
        "Digital signature implementations"
      ],
      howToVerify: "Check if systems have checksums, digital signatures, or version control for PHI. Test if modifications to data are detected and logged. Verify backup integrity verification procedures.",
      whatToDocument: "Integrity control mechanisms in place, testing results, version control implementation, digital signature usage, backup verification procedures"
    }
  },
  {
    id: 'Q32',
    section: 'technical',
    question: 'Do you use network segmentation to isolate systems with ePHI?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes, segmented networks with VLANs/VPCs', description: '', points: 3 },
      { value: 'partial', label: 'Partial segmentation', description: '', points: 1 },
      { value: 'no', label: 'No network segmentation', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '2026 HIPAA Security Rule update - Network segmentation required',
    required: true,
    weight: 3,
    tooltipContent: {
      plainEnglish: "Network segmentation divides your network into separate zones, and systems in each zone can only communicate with other systems in the same zone or adjacent zones (with explicit rules). This limits the blast radius of a breach: if an attacker compromises a non-clinical workstation, they can't immediately jump to the EHR server because they're on different network segments with restricted communication. For example, a typical healthcare network might have: internet-facing zone (public website), administrative zone (billing, HR), clinical zone (EHR, patient data), and isolated zone (critical systems like backup servers). Firewalls between zones enforce rules about what traffic is allowed.",
      hipaaReference: "2026 HIPAA Security Rule Update - Network Segmentation emphasized as critical control; previously addressable, now recommended as required. Status: Required (as of 2026 update); was addressable. Effective Date: 2026 Security Rule update (January 2025 final rule). 60% of ransomware attacks spread laterally through networks without segmentation. Network segmentation was optional; 2026 update makes it expected/required. Segmentation significantly reduces breach impact and response time. OCR increasingly cites lack of segmentation in audits; this will become automatic citation post-2026.",
      realWorldExample: "A clinic has a flat network: all computers (EHR, patient portal, billing, internet browsing) on the same network segment. A user downloads malware; it spreads to all systems including EHR. Breach. Fix: segment network into clinical zone (EHR only), administrative zone (billing), and public zone (internet/patient portal). A hospital uses VLANs: VLAN 100 for clinical staff EHR access, VLAN 200 for administrative staff, VLAN 300 for guest WiFi. Firewall rules restrict communication between VLANs. Clinical zone is isolated from guest traffic. An attacker compromises a guest WiFi user; they can't access clinical systems because of firewall rules.",
      estimatedCost: {
        small: "$2,000-5,000 (VLAN configuration)",
        medium: "$20,000-100,000+ (new firewall/switch hardware)",
        large: "$50,000-200,000+ (zero-trust architecture)"
      },
      implementationSteps: [
        "Assessment and design: 2-4 weeks",
        "Implementation: 4-8 weeks",
        "Testing and validation: 2 weeks",
        "Monitoring and tuning: ongoing"
      ],
      commonMistakes: [
        "Creating segments but not enforcing rules between them",
        "Having too many exceptions that defeat the purpose",
        "Not monitoring traffic between segments",
        "Implementing segmentation without proper planning"
      ],
      resourceLinks: [
        { title: "HHS Transmission Security", url: "https://www.hhs.gov/hipaa/for-professionals/security/laws-regulations/index.html", type: "external" }
      ],
            timeToComplete: {
        small: "4-8 weeks",
        medium: "8-16 weeks",
        large: "16-24 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "Network Administrator",
        "IT Security Manager",
        "Infrastructure Manager",
        "Firewall Administrator"
      ],
      whereToLook: [
        "Network topology diagrams",
        "VLAN configuration",
        "Firewall rules and policies",
        "Network monitoring tools"
      ],
      howToVerify: "Review network diagrams to see if systems are segmented. Check firewall rules between segments. Test if systems in different segments can communicate inappropriately.",
      whatToDocument: "Network segmentation design, VLAN configuration, firewall rules between segments, testing results, network monitoring capabilities"
    }
  }
];
